import { useLocalStorageState } from "@v2/hooks/useLocalStorage";
import { z } from "zod";

const LOGIN_AS_KEY = "replo.debug.loginAs";

/**
 * The `?opid` URL param carries a "user operator id" — the first two uuid
 * segments (`xxxxxxxx-xxxx`), the short handle a superuser uses to operate as
 * another user. The backend resolver accepts any uuid prefix (up to the full
 * id), but URLs carry the two-segment form.
 */
export const LOGIN_AS_PARAM = "opid";

/** User operator id (first two uuid segments, e.g. `123e4567-e89b`). */
export function getUserOperatorId(userId: string): string {
  return userId.split("-").slice(0, 2).join("-");
}

/**
 * Whether an opid (any uuid prefix — one segment, two segments, or the full
 * id) refers to the given user. Exact string comparison against the
 * two-segment form is wrong here: a user's own full uuid would read as
 * "foreign" and get offered as a login-as target for themselves.
 */
export function operatorIdMatchesUser(
  operatorId: string,
  userId: string,
): boolean {
  return userId.toLowerCase().startsWith(operatorId.toLowerCase());
}

/**
 * Login-as state persisted in localStorage (the "sandbox override"):
 * - `candidate`: a superuser opened a URL with a foreign `?opid`; we
 *   offer the swap but haven't performed it yet.
 * - `active`: the token swap happened; we hold the original token so the
 *   superuser can exit back to their own session.
 */
const loginAsStateSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("candidate"),
    guestUserOperatorId: z.string(),
  }),
  z.object({
    status: z.literal("active"),
    guestUserOperatorId: z.string().optional(),
    originalUserOperatorId: z.string().optional(),
    guestEmail: z.string(),
    originalEmail: z.string().optional(),
    originalName: z.string().nullish(),
    originalToken: z.string(),
    // NOTE (Gabe, 2026-07-01): A foreign `?opid` opened while already
    // impersonating. We can't act on it directly (the session holds the
    // guest's token and login-as routes are superuser-gated), so we stash it
    // and offer a switch in the chrome.
    pendingGuestUserOperatorId: z.string().optional(),
  }),
]);

export function useLoginAsState() {
  return useLocalStorageState(LOGIN_AS_KEY, null, {
    schema: loginAsStateSchema.nullable(),
  });
}
